/**
 * Role 14 — Statement of Profit or Loss by Nature — calculated totals.
 *
 * Extracted verbatim from the former single-class `CalculationService`, which held
 * all 13 sheets' total calculations in one 5,557-line file. Each function is pure and
 * self-contained (no shared state, no `this`), so the split is a move, not a rewrite.
 * `CalculationService` remains the injectable entry point and delegates here.
 */

/**
 * Calculate all Role14 (320000 - Statement of Profit or Loss by Nature) totals.
 *
 * Sign convention preserved from the component: expense line items are wrapped
 * in `Math.abs(...)` before subtraction so the formula stays correct whether
 * the user typed expenses as positive or negative. `inventoryChanges` is the
 * one exception — it keeps its sign (an increase in inventory is added to
 * profit, a decrease subtracts).
 *
 * Suffixes are bare strings like 'GroupCurrent' / 'CompanyPrevious' / 'Current' —
 * role14 does not use the bracketed `[Consolidated][CurrentYear]` form.
 *
 * @param formData - The form data object (control name → value)
 * @param suffixes - Resolved column suffixes
 * @returns Object containing only the calculated total fields
 */
export function calculateTotals_320000(
  formData: Record<string, any>,
  suffixes: ReadonlyArray<string>,
): Record<string, any> {
  const calculatedTotals: Record<string, any> = {};

  const num = (key: string): number => {
    const raw = formData[key];
    if (raw === null || raw === undefined || raw === '') return 0;
    const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  for (const suffix of suffixes) {
    // 1. Profit from Operations
    const revenue = num(`ssmt:RevenueByNature${suffix}`);
    const otherIncome = num(`ssmt:OtherIncomeByNature${suffix}`);
    const inventoryChanges = num(
      `ssmt:ChangesInInventoriesOfFinishedGoodsAndWorkInProgressByNature${suffix}`,
    );
    const rawMaterials = Math.abs(
      num(`ssmt:RawMaterialsAndConsumablesUsedByNature${suffix}`),
    );
    const employeeBenefits = Math.abs(
      num(`ssmt:EmployeeBenefitsExpenseByNature${suffix}`),
    );
    const depreciation = Math.abs(
      num(`ssmt:DepreciationAndAmortisationExpenseByNature${suffix}`),
    );
    const impairment = Math.abs(
      num(`ssmt:ImpairmentLossRecognisedInProfitOrLoss${suffix}`),
    );
    const transportation = Math.abs(
      num(`ssmt:TransportationExpense${suffix}`),
    );
    const advertising = Math.abs(
      num(`ssmt:AdvertisingAndMarketingExpense${suffix}`),
    );
    const rental = Math.abs(num(`ssmt:RentalExpenseByNature${suffix}`));
    const utilities = Math.abs(num(`ssmt:UtilitiesExpenseByNature${suffix}`));
    const professionalFees = Math.abs(
      num(`ssmt:ProfessionalFeesExpense${suffix}`),
    );
    const insurance = Math.abs(num(`ssmt:InsuranceExpenseByNature${suffix}`));
    const foreignExchange = Math.abs(
      num(`ssmt:ForeignExchangeLossByNature${suffix}`),
    );
    const otherExpenses = Math.abs(
      num(`ssmt:OtherExpensesByNature${suffix}`),
    );

    const profitFromOperations =
      revenue +
      otherIncome +
      inventoryChanges -
      rawMaterials -
      employeeBenefits -
      depreciation -
      impairment -
      transportation -
      advertising -
      rental -
      utilities -
      professionalFees -
      insurance -
      foreignExchange -
      otherExpenses;
    calculatedTotals[`ssmt:ProfitFromOperationsByNature${suffix}`] =
      profitFromOperations;

    // 2. Profit Before Tax
    const financeIncome = num(`ssmt:FinanceIncomeByNature${suffix}`);
    const financeCosts = Math.abs(num(`ssmt:FinanceCostsByNature${suffix}`));
    const shareOfAssociates = num(
      `ssmt:ShareOfProfitOfAssociatesByNature${suffix}`,
    );
    const shareOfJVs = num(
      `ssmt:ShareOfProfitOfJointVenturesByNature${suffix}`,
    );
    const profitBeforeTax =
      profitFromOperations +
      financeIncome -
      financeCosts +
      shareOfAssociates +
      shareOfJVs;
    calculatedTotals[`ssmt:ProfitBeforeTaxByNature${suffix}`] =
      profitBeforeTax;

    // 3. Profit for the Year
    const taxExpense = Math.abs(
      num(`ssmt:IncomeTaxExpenseByNature${suffix}`),
    );
    calculatedTotals[`ssmt:ProfitForTheYearByNature${suffix}`] =
      profitBeforeTax - taxExpense;

    // 4. Total Profit Attribution
    const profitToOwners = num(
      `ssmt:ProfitAttributableToOwnersOfParentByNature${suffix}`,
    );
    const profitToNCI = num(
      `ssmt:ProfitAttributableToNonControllingInterestsByNature${suffix}`,
    );
    calculatedTotals[`ssmt:TotalProfitAttributionByNature${suffix}`] =
      profitToOwners + profitToNCI;
  }

  return calculatedTotals;
}
